import React, { useEffect, useState } from 'react';
import { Autoplay, Keyboard, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import './slider.css';

interface IDoingCourse {
  id: string;
  slides: Array<React.JSX.Element>;
  slidesPerView?: 'auto' | number;
  spaceBetween?: number;
  keyboard?: boolean;
  className?: string;
  showPagination?: boolean;
  /**
   * Rotação manual: sempre exibe 3 slides e, a cada `rotateInterval`,
   * remove o primeiro e adiciona o próximo (loop), em vez do loop nativo
   * do Swiper. Usado pelo MarketingBanners.
   */
  autoRotate?: boolean;
  rotateInterval?: number;
}

const VISIBLE_COUNT = 3;

export default function Slider({
  id,
  slides = [],
  slidesPerView = 'auto',
  spaceBetween = 32,
  keyboard = true,
  className,
  showPagination = true,
  autoRotate = false,
  rotateInterval = 7500,
}: IDoingCourse): React.JSX.Element {
  const itemsPerView = typeof slidesPerView === 'number' ? slidesPerView : 3;

  // Modo de rotação manual: só faz sentido quando há mais slides do que a
  // janela visível (senão não há o que rotacionar).
  const isRotating = autoRotate && slides.length > VISIBLE_COUNT;

  const [startIndex, setStartIndex] = useState(0);

  useEffect(() => {
    if (!isRotating) {
      return;
    }

    const interval = setInterval(() => {
      // Remove o primeiro e adiciona o próximo (wrap-around).
      setStartIndex((prev) => (prev + 1) % slides.length);
    }, rotateInterval);

    return () => clearInterval(interval);
  }, [isRotating, slides.length, rotateInterval]);

  // Janela de 3 slides consecutivos com wrap-around para o modo manual.
  const rotatingSlides = Array.from({ length: VISIBLE_COUNT }, (_, i) => {
    return slides[(startIndex + i) % slides.length];
  });

  // "Muitos itens": mais itens do que cabem na viewport. Só então habilita
  // o loop + autoplay nativos. No modo manual, o Swiper fica estático.
  const hasManyItems = !isRotating && slides.length > itemsPerView;

  // Com loop nativo precisamos de um slide clonado extra; sem loop (poucos
  // itens), renderiza apenas os slides reais para o justify-between alinhar.
  const renderedSlides = isRotating
    ? rotatingSlides
    : hasManyItems
    ? [...slides, ...slides.slice(0, 1)]
    : slides;

  const useJustifyBetween = isRotating || slides.length >= VISIBLE_COUNT;

  // No modo de rotação usamos dots customizados (a paginação nativa só
  // enxergaria os 3 slides visíveis, não a lista completa).
  const useSwiperPagination = showPagination && !isRotating;
  const showRotateDots = showPagination && isRotating && slides.length > 1;

  return (
    <div className={`relative ${className}`}>
      <Swiper
        id={id}
        slidesPerView={slidesPerView}
        spaceBetween={useJustifyBetween ? 0 : spaceBetween}
        centerInsufficientSlides={!useJustifyBetween}
        mousewheel={false}
        keyboard={keyboard}
        autoplay={
          hasManyItems ? { delay: 7500, disableOnInteraction: false } : false
        }
        loop={hasManyItems}
        modules={[Navigation, Pagination, Keyboard, Autoplay]}
        navigation={{
          nextEl: `.swiper-button-next-custom-${id}`,
          prevEl: `.swiper-button-prev-custom-${id}`,
        }}
        pagination={
          useSwiperPagination
            ? {
                clickable: true,
                el: `.swiper-pagination-${id}`,
              }
            : false
        }
        className={`w-full ${
          useJustifyBetween ? 'sm:[&_.swiper-wrapper]:justify-between' : ''
        }`}
      >
        {renderedSlides.map((Elem, index) => {
          return (
            <SwiperSlide
              className='!w-full [&>*]:w-full sm:!w-fit'
              key={`${id}-${index}`}
            >
              {isRotating ? (
                // key com startIndex re-monta o wrapper a cada rotação,
                // reexecutando a animação de entrada.
                <div
                  className='slider-rotate-item w-full'
                  key={`${id}-rotate-${startIndex}-${index}`}
                >
                  {Elem}
                </div>
              ) : (
                Elem
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>

      {useSwiperPagination && (
        <div
          className={`swiper-pagination-${id} slider-swiper-pagination mt-4 flex justify-center gap-1`}
        ></div>
      )}

      {showRotateDots && (
        <div className='slider-swiper-pagination mt-4 flex justify-center gap-1'>
          {slides.map((_, i) => {
            const isActive = i === startIndex;
            return (
              <button
                key={`${id}-dot-${i}`}
                type='button'
                aria-label={`Ir para o slide ${i + 1}`}
                aria-current={isActive}
                onClick={() => setStartIndex(i)}
                className={`slider-rotate-dot swiper-pagination-bullet ${
                  isActive ? 'swiper-pagination-bullet-active' : ''
                }`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
